import React, { useState, useEffect } from "react";
import "./Dashboard.css";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { LogoutEmp, GetEmployeeName } from "../../services/authService";
import ConfirmationAlert from "../AlertComponent/ConfirmationAlert";
import NormalAlert from "../AlertComponent/NormalAlert";

const Sidebar = ({
  isOpen,
  setIsOpen,
  isPinned,
  setIsPinned,
  onSelectMenu,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [UserName, setUserName] = useState(Cookies.get("username") || "User");
  const [confirmAlert, setConfirmAlert] = useState(null);
  const [isJobMenuOpen, setIsJobMenuOpen] = useState(false);
  const [isReportIssueOpen, setIsReportIssueOpen] = useState(false);
  const [isAdminMenuOpen, setIsAdminMenuOpen] = useState(false);
  const [isCandidateMenuOpen, setIsCandidateMenuOpen] = useState(false);
  const [employeeRole, setEmployeeRole] = useState(null);
  const [alert, setAlert] = useState(null);
  const finalSidebarState = isPinned ? true : isOpen;

  const Token = Cookies.get("employeeToken");

  //  Auto-open Admin menu
  useEffect(() => {
    if (
      location.pathname === "/Dashboard/Add-Consultant" ||
      location.pathname === "/Dashboard/Add-Employee"
    ) {
      setIsAdminMenuOpen(true);
    } else {
      setIsAdminMenuOpen(false);
    }
  }, [location.pathname]);

  //  Auto-open Job menu
  useEffect(() => {
    if (
      location.pathname === "/Dashboard/PostJob" ||
      location.pathname === "/Dashboard/Draft-Jobs" ||
      location.pathname === "/Dashboard/Live-Jobs"
    ) {
      setIsJobMenuOpen(true);
    } else {
      setIsJobMenuOpen(false);
    }
  }, [location.pathname]);

  //  Auto-open Candidate menu
  useEffect(() => {
    const candidatePaths = [
      "/Dashboard/New-Applications",
      "/Dashboard/Shortlist-Candidates",
      "/Dashboard/Interview-Scheduled",
      "/Dashboard/Assessment-Candidates",
      "/Dashboard/Get-Selected-Candidates",
      "/Dashboard/Rejected-Candidates",
      "/Dashboard/Hold-Candidates",
      "/Dashboard/BGV-Candidates",
    ];
    if (candidatePaths.includes(location.pathname)) {
      setIsCandidateMenuOpen(true);
    } else {
      setIsCandidateMenuOpen(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (
      location.pathname === "/Dashboard/Report-Issue"
    ) {
      setIsReportIssueOpen(true);
    } else {
      setIsReportIssueOpen(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    const fetchUserName = async () => {
      const token = Cookies.get("employeeToken");
      if (!token) {
        setUserName("User");
        Cookies.remove("username");
        setEmployeeRole(null);
        return;
      }

      try {
        const response = await GetEmployeeName(token);
        if (response?.data?.status === "Success" && response?.data?.name) {
          const name = response.data.name;
          const role = response.data.employeeRole;

          Cookies.set("username", name, { secure: true, sameSite: "Strict" });
          setUserName(name);
          setEmployeeRole(role);
        } else {
          setUserName("User");
          setEmployeeRole(null);
          Cookies.remove("username");
        }
      } catch (error) {
        setAlert({
          type: "fail",
          message:
            error?.response?.data?.message ||
            "Something went wrong. Please try again.",
        });
        setUserName("User");
        setEmployeeRole(null);
        Cookies.remove("username");
      }
    };

    fetchUserName();
  }, []);

  //  Logout
  const handleLogout = () => {
    setConfirmAlert({
      type: "warning",
      message: "Are you sure you want to logout?",
    });
  };

  const handleConfirmLogout = async () => {
    try {
      await LogoutEmp(new Date());
    } catch (error) {
      const safeMessage =
        error?.response?.data?.message ||
        "Something went wrong. Please try again.";

      setAlert({
        type: "fail",
        message: safeMessage,
      });
    } finally {
      Cookies.remove("employeeToken");
      Cookies.remove("username");
      window.location.href = "/";
    }
  };

  const handleCancelLogout = () => setConfirmAlert(null);

  const handleMenuClick = (menuKey) => {
    onSelectMenu(menuKey);
    setIsJobMenuOpen(false);
  };

  const handleCandidateMenuClick = (menuKey) => {
    onSelectMenu(menuKey);
    setIsCandidateMenuOpen(false);
  };

  return (
    <>
      <div
        className={`sidebar 
    ${finalSidebarState ? "show" : ""} 
    ${isPinned ? "pinned" : ""} 
    ${window.innerWidth < 768 && !isPinned ? "mobile-sidebar" : ""}
  `}
      >
        {/* User Info */}
        <div className="p-4 border-bottom d-flex align-items-center gap-3">
          <img
            src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
              UserName
            )}&background=0D47A1&color=fff`}
            className="rounded-circle border border-white"
            width={40}
            alt={UserName}
          />
          <div className="profile-text">
            <div className="fw-bold text-white small">Welcome</div>
            <div className="text-white-50 small">{UserName}</div>
          </div>

          {/* PIN BUTTON */}
          <button
            className="btn btn-sm btn-outline-light ms-auto"
            onClick={() => {
              const newValue = !isPinned;
              setIsPinned(newValue);
              Cookies.set("sidebarPinned", newValue ? "true" : "false", {
                secure: true,
                sameSite: "Strict",
                expires: 30,
              });
            }}
            title={isPinned ? "Unpin Sidebar" : "Pin Sidebar"}
          >
            <i className={`bi ${isPinned ? "bi-pin-angle-fill" : "bi-pin"}`} />
          </button>
        </div>

        {/* Sidebar Menu */}
        <nav className="nav flex-column mt-3 px-3 gap-1">
          <Link
            to="/Dashboard"
            onClick={() => onSelectMenu("dashboard")}
            className="nav-link text-white d-flex align-items-center sidebar-item"
          >
            <i className="bi bi-speedometer2 me-2"></i>
            <span className="sidebar-label">Dashboard</span>
          </Link>

          {/* Admin Menu */}
          {employeeRole === "Admin Employee" && (
            <>
              {/* Admin Menu */}
              <div
                className="sidebar-item"
                onClick={() => setIsAdminMenuOpen(!isAdminMenuOpen)}
              >
                <button className="nav-link text-white d-flex align-items-center w-100">
                  <i className="bi bi-person-add me-2" />
                  <span className="sidebar-label">Admin</span>
                  <i
                    className={`ms-3 bi ${
                      isAdminMenuOpen ? "bi-chevron-up" : "bi-chevron-down"
                    }`}
                  />
                </button>
              </div>

              <div
                className={`sidebar-submenu ${isAdminMenuOpen ? "open" : ""}`}
              >
                <Link
                  to="/Dashboard/Add-Consultant"
                  onClick={() => handleMenuClick("addConsultant")}
                  className="submenu-item"
                >
                  <i className="bi bi-person-workspace me-2" />
                  <span className="sidebar-label">Add Consultant</span>
                </Link>

                <Link
                  to="/Dashboard/Add-Employee"
                  onClick={() => handleMenuClick("addEmployee")}
                  className="submenu-item"
                >
                  <i className="bi bi-person-plus me-2" />
                  <span className="sidebar-label">Add Employee</span>
                </Link>

                <Link
                  to="/Dashboard/Master-Data"
                  onClick={() => handleMenuClick("masterData")}
                  className="submenu-item"
                >
                  <i className="bi bi-person-plus me-2" />
                  <span className="sidebar-label">Master Data</span>
                </Link>
              </div>
            </>
          )}

          {/* Job Menu */}
          <div
            className="sidebar-item"
            onClick={() => setIsJobMenuOpen(!isJobMenuOpen)}
          >
            <button className="nav-link text-white d-flex align-items-center w-100">
              <i className="bi bi-briefcase me-2" />
              <span className="sidebar-label">Job</span>
              <i
                className={`ms-3 bi ${
                  isJobMenuOpen ? "bi-chevron-up" : "bi-chevron-down"
                }`}
              />
            </button>
          </div>

          <div className={`sidebar-submenu ${isJobMenuOpen ? "open" : ""}`}>
            <Link
              to="/Dashboard/PostJob"
              onClick={() => handleMenuClick("postJob")}
              className="submenu-item"
            >
              <i className="bi bi-megaphone me-2" />
              <span className="sidebar-label">Post Job</span>
            </Link>
            <Link
              to="/Dashboard/Draft-Jobs"
              onClick={() => handleMenuClick("viewcreatedJob")}
              className="submenu-item"
            >
              <i className="bi bi-card-list me-2" />
              <span className="sidebar-label">Draft Jobs</span>
            </Link>
            <Link
              to="/Dashboard/Live-Jobs"
              onClick={() => handleMenuClick("viewpostedJob")}
              className="submenu-item"
            >
              <i className="bi bi-broadcast me-2" />
              <span className="sidebar-label">Live Jobs</span>
            </Link>
          </div>

          {/* Candidate Menu */}
          <div
            className="sidebar-item"
            onClick={() => setIsCandidateMenuOpen(!isCandidateMenuOpen)}
          >
            <button className="nav-link text-white d-flex align-items-center w-100">
              <i className="bi bi-person me-2" />
              <span className="sidebar-label">Candidate</span>
              <i
                className={`ms-3 bi ${
                  isCandidateMenuOpen ? "bi-chevron-up" : "bi-chevron-down"
                }`}
              />
            </button>
          </div>

          <div
            className={`sidebar-submenu ${isCandidateMenuOpen ? "open" : ""}`}
          >
            <Link
              to="/Dashboard/New-Applications"
              onClick={() => handleCandidateMenuClick("NewApplication")}
              className="submenu-item"
            >
              <i className="bi bi-inbox me-2" />
              <span className="sidebar-label">Incoming Applications</span>
            </Link>
            <Link
              to="/Dashboard/Shortlist-Candidates"
              onClick={() => handleCandidateMenuClick("ShortlistCandidates")}
              className="submenu-item"
            >
              <i className="bi bi-bookmark-check me-2" />
              <span className="sidebar-label">Shortlisted</span>
            </Link>
            <Link
              to="/Dashboard/Interview-Scheduled"
              onClick={() => handleCandidateMenuClick("InterviewScheduled")}
              className="submenu-item"
            >
              <i className="bi bi-calendar-event me-2" />
              <span className="sidebar-label">Interviews</span>
            </Link>
            <Link
              to="/Dashboard/Assessment-Candidates"
              onClick={() => handleCandidateMenuClick("AssessmentCandidates")}
              className="submenu-item"
            >
              <i className="bi bi-journal-check me-2" />
              <span className="sidebar-label">Assessments</span>
            </Link>
            <Link
              to="/Dashboard/Get-Selected-Candidates"
              onClick={() => handleCandidateMenuClick("GetSelectedCandidate")}
              className="submenu-item"
            >
              <i className="bi bi-patch-check me-2" />
              <span className="sidebar-label">Selected</span>
            </Link>
            <Link
              to="/Dashboard/Get-Offer-Released-Candidates"
              onClick={() => handleCandidateMenuClick("GetOfferReleasedCandidates")}
              className="submenu-item"
            >
              <i className="bi bi-calendar-event me-2" />
              <span className="sidebar-label">Offer Released Candidate</span>
            </Link>
            <Link
              to="/Dashboard/Get-Hired-Candidates"
              onClick={() => handleCandidateMenuClick("GetHiredCandidates")}
              className="submenu-item"
            >
              <i className="bi bi-calendar-event me-2" />
              <span className="sidebar-label">Hired Candidates</span>
            </Link>
            <Link
              to="/Dashboard/Rejected-Candidates"
              onClick={() => handleCandidateMenuClick("RejectedCandidate")}
              className="submenu-item"
            >
              <i className="bi bi-x-octagon me-2" />
              <span className="sidebar-label">Rejected</span>
            </Link>
              <Link
              to="/Dashboard/Get-Back-Out-Candidates"
              onClick={() => handleCandidateMenuClick("GetBackOutCandidates")}
              className="submenu-item"
            >
              <i className="bi bi-calendar-event me-2" />
              <span className="sidebar-label">Back-Out Candidates</span>
            </Link>
            <Link
              to="/Dashboard/Hold-Candidates"
              onClick={() => handleCandidateMenuClick("HoldCandidate")}
              className="submenu-item"
            >
              <i className="bi bi-hourglass-split me-2" />
              <span className="sidebar-label">On Hold</span>
            </Link>
            <Link
              to="/Dashboard/BGV-Candidates"
              onClick={() => handleCandidateMenuClick("BGVCandidate")}
              className="submenu-item"
            >
              <i className="bi bi-shield-check me-2" />
              <span className="sidebar-label">Background Verification</span>
            </Link>
          </div>

           <div
            className="sidebar-item"
            onClick={() => setIsReportIssueOpen(!isReportIssueOpen)}
          >
            <button className="nav-link text-white d-flex align-items-center w-100">
              <i className="bi bi-bug me-2" />
              <span className="sidebar-label">Report Issue</span>
              <i
                className={`ms-3 bi ${
                  isReportIssueOpen ? "bi-chevron-up" : "bi-chevron-down"
                }`}
              />
            </button>
          </div>

          <div className={`sidebar-submenu ${isReportIssueOpen ? "open" : ""}`}>
            <Link
              to="/Dashboard/Report-Issue"
              onClick={() => handleMenuClick("ReportIssue")}
              className="submenu-item"
            >
              <i className="bi bi-bug me-2" />
              <span className="sidebar-label">Report Issue</span>
            </Link>
          </div>
        </nav>

        {/* Logout Button */}
        <div className="logout px-3 py-2">
          <button
            onClick={handleLogout}
            className="btn btn-light w-100 d-flex align-items-center justify-content-start gap-2"
          >
            <i className="bi bi-box-arrow-right fs-5" />
            <span className="sidebar-label">Logout</span>
          </button>
        </div>

        {/* Confirmation Alert */}
        {confirmAlert && (
          <ConfirmationAlert
            title={confirmAlert.type}
            message={confirmAlert.message}
            onClose={() => setConfirmAlert(null)}
            onConfirm={handleConfirmLogout}
            onCancel={handleCancelLogout}
          />
        )}
      </div>
      {alert && (
        <NormalAlert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}
    </>
  );
};

export default Sidebar;
